import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

export function CompactAudioPlayer({ url, name, compact = false, accentColor = '#1075ae' }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [url]);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onTime = () => {
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [url]);

  const togglePlayback = useCallback(() => {
    if (Platform.OS === 'web') {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) {
        audio.play().catch(() => {});
      } else {
        audio.pause();
      }
      return;
    }
    if (url) Linking.openURL(url).catch(() => {});
  }, [url]);

  if (!url) return null;

  return (
    <View style={[styles.wrap, compact && styles.wrapCompact]}>
      <TouchableOpacity
        onPress={togglePlayback}
        style={[styles.playBtn, { backgroundColor: accentColor }, compact && styles.playBtnCompact]}
        activeOpacity={0.85}
        accessibilityLabel={playing ? 'Pause audio' : 'Play audio'}
      >
        <Ionicons
          name={playing ? 'pause' : 'play'}
          size={compact ? 11 : 15}
          color="#fff"
          style={playing ? undefined : { marginLeft: compact ? 1 : 2 }}
        />
      </TouchableOpacity>

      <View style={[styles.meta, compact && styles.metaCompact]}>
        {!compact && name ? (
          <Text style={styles.name} numberOfLines={1}>
            {name}
          </Text>
        ) : null}
        <View style={[styles.track, compact && styles.trackCompact]}>
          <View style={[styles.fill, { width: `${Math.max(0, Math.min(100, progress * 100))}%`, backgroundColor: accentColor }]} />
        </View>
      </View>

      {Platform.OS === 'web' ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio ref={audioRef} src={url} preload="metadata" style={{ display: 'none' }} />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 6,
    paddingVertical: 6,
  },
  wrapCompact: {
    gap: 6,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  playBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  playBtnCompact: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  meta: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  metaCompact: {
    gap: 0,
  },
  name: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    color: '#444',
    textAlign: 'left',
  },
  track: {
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(0,0,0,0.1)',
    overflow: 'hidden',
    width: '100%',
  },
  trackCompact: {
    height: 3,
  },
  fill: {
    height: '100%',
    borderRadius: 2,
  },
});
